import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, FileChips, formatDate, DeadlineWarning, UploadProgress, dropProps, deadlineLabel } from "@/components/shared";
import { MessageThread } from "@/components/ProjectMessages";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Scale, FilePlus, Pencil, Paperclip, Loader2, Phone, Mail } from "lucide-react";

const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  "Ofisler": "bg-orange-500/10 text-orange-600 border-orange-500/20",
};

const ROLE_TR = { superadmin: "Yönetici", assigner: "Atayıcı", uretici: "Üretici" };

export default function ProjectDetailPage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [noteEdit, setNoteEdit] = useState(false);
  const [noteVal, setNoteVal] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [prodLog, setProdLog] = useState([]);
  const fileInputRef = useRef(null);

  const addFiles = async (e) => {
    const files = e?.target?.files || e;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadSmart(files, setProgress);
      const { data } = await api.patch(`/projects/${id}`, { add_file_ids: uploaded.map((f) => f.id) });
      setProject((p) => ({ ...p, files: data.files }));
      toast.success("Dosya(lar) eklendi");
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (fileId) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, { remove_file_ids: [fileId] });
      setProject((p) => ({ ...p, files: data.files }));
      toast.success("Dosya kaldırıldı");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      await api.patch(`/projects/${id}`, { note: noteVal });
      setProject((p) => ({ ...p, note: noteVal.trim() }));
      setNoteEdit(false);
      toast.success("Not güncellendi");
    } catch {
      toast.error("Not kaydedilemedi");
    } finally {
      setNoteSaving(false);
    }
  };

  useEffect(() => {
    api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => setNotFound(true));
    api.get(`/projects/${id}/production-log`).then((r) => setProdLog(r.data || [])).catch(() => {});
  }, [id]);

  if (notFound) return <p className="text-slate-500 text-sm">Proje bulunamadı.</p>;
  if (!project) return <p className="text-slate-500 text-sm">Yükleniyor...</p>;

  const req = project.request;

  return (
    <div className="space-y-8 fade-in max-w-4xl">
      <div>
        <Link to="/admin/projeler" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200" data-testid="back-to-projects">
          <ArrowLeft size={15} /> Tüm Projelere Dön
        </Link>
        <div className="flex items-end justify-between gap-4 mt-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Proje Detayı</div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1" data-testid="project-detail-name">{project.name}</h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-slate-500 flex-wrap">
              <Badge variant="outline" className={`rounded-none text-xs ${zoneCls[project.zone] || "border-slate-300 text-slate-600"}`}>{project.zone || project.category || "-"}</Badge>
              {project.zone ? (
                <>
                  <span>Stand {project.stand_no}</span>
                  <span>· {project.project_type}</span>
                  {project.area_m2 && <span>· {project.area_m2} m²</span>}
                </>
              ) : (
                <>
                  {project.location && <span>{project.location}</span>}
                  {project.event_date && <span>· {project.event_date}</span>}
                </>
              )}
              <span>· Oluşturulma: {formatDate(project.created_at)}</span>
            </div>
          </div>
        </div>
      </div>

      {(project.sem_contact || project.cm_contact) && (
        <div className="border border-slate-200 bg-white p-5" data-testid="project-semcm-panel">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">SEM / CM</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["SEM", project.sem_contact], ["CM", project.cm_contact]].map(([k, c]) => (
              <div key={k} className="border border-slate-100 bg-slate-50/50 p-4 flex items-start gap-3" data-testid={`project-${k.toLowerCase()}-card`}>
                <Badge variant="outline" className={`rounded-none text-[10px] shrink-0 ${k === "SEM" ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-blue-500/10 text-blue-700 border-blue-500/30"}`}>{k}</Badge>
                {c ? (
                  <div className="min-w-0 space-y-1">
                    <div className="font-semibold text-sm text-slate-900">{c.name}</div>
                    {c.phone && (
                      <a href={`tel:${c.phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 transition-colors duration-150" data-testid={`project-${k.toLowerCase()}-phone`}>
                        <Phone size={12} /> {c.phone}
                      </a>
                    )}
                    {c.email && (
                      <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 transition-colors duration-150 truncate" data-testid={`project-${k.toLowerCase()}-email`}>
                        <Mail size={12} /> {c.email}
                      </a>
                    )}
                    {!c.phone && !c.email && <div className="text-xs text-slate-400">İletişim bilgisi yok</div>}
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 pt-0.5">Atanmamış</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid="project-note-panel">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500">Not</div>
          {!noteEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setNoteVal(project.note || ""); setNoteEdit(true); }}
              data-testid="edit-note-button"
              className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 h-8 transition-colors duration-200"
            >
              <Pencil size={12} /> {project.note ? "Düzenle" : "Not Ekle"}
            </Button>
          )}
        </div>
        {noteEdit ? (
          <div className="space-y-3">
            <Textarea
              value={noteVal}
              onChange={(e) => setNoteVal(e.target.value)}
              rows={4}
              placeholder="Proje ile ilgili iç notlarınız..."
              data-testid="note-edit-input"
              className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm"
            />
            <div className="flex items-center gap-2">
              <Button onClick={saveNote} disabled={noteSaving} data-testid="note-save-button" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 h-9 font-semibold transition-colors duration-200">
                {noteSaving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
              <Button variant="outline" onClick={() => setNoteEdit(false)} data-testid="note-cancel-button" className="rounded-none border-slate-200 hover:bg-slate-100 h-9 transition-colors duration-200">
                Vazgeç
              </Button>
            </div>
          </div>
        ) : project.note ? (
          <p className="text-sm text-slate-700 whitespace-pre-line" data-testid="project-note-text">{project.note}</p>
        ) : (
          <p className="text-sm text-slate-400">Henüz not eklenmedi.</p>
        )}
      </div>

      {prodLog.length > 0 && (
        <div className="border border-slate-200 bg-white p-5" data-testid="production-timeline-panel">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">Üretim Zaman Çizelgesi</div>
            {prodLog[0]?.status && (
              <span className="inline-flex items-center h-7 px-2.5 text-xs font-semibold border bg-indigo-500/10 text-indigo-700 border-indigo-500/30" data-testid="production-current-status">
                {prodLog[0].status}
              </span>
            )}
          </div>
          <ol className="relative border-l border-slate-200 ml-1.5 space-y-5">
            {prodLog.map((l, i) => (
              <li key={l.id} className="ml-5 relative" data-testid={`prod-log-${l.id}`}>
                <span className={`absolute -left-[25px] top-1 w-2.5 h-2.5 rounded-full ${i === 0 ? "bg-indigo-600 ring-4 ring-indigo-100" : l.status ? "bg-indigo-300" : "bg-slate-300"}`} />
                <div className={`text-sm font-semibold ${i === 0 ? "text-slate-900" : "text-slate-600"}`}>
                  {l.status || "Durum kaldırıldı"}
                  {l.prev_status && l.status && <span className="text-xs font-normal text-slate-400 ml-1.5">({l.prev_status} →)</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {new Date(l.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {" · "}{l.changed_by_name} <span className="text-slate-400">({ROLE_TR[l.changed_by_role] || l.changed_by_role})</span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div {...dropProps(addFiles, setDragOver)} className={`border p-5 space-y-3 transition-colors duration-200 ${dragOver ? "border-dashed border-slate-900 bg-slate-100" : "border-slate-200 bg-white"}`} data-testid="project-files-panel">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500">Proje Dosyaları {dragOver && <span className="text-slate-900 font-semibold normal-case">— dosyaları buraya bırakın</span>}</div>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={addFiles} data-testid="project-file-input" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="add-project-file-button"
            className="rounded-none border-slate-200 hover:bg-slate-100 gap-1.5 h-8 transition-colors duration-200"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Paperclip size={12} />} Dosya Ekle
          </Button>
        </div>
        {uploading && <UploadProgress progress={progress} />}
        <FileChips files={project.files} onRemove={removeFile} />
      </div>

      {req ? (
        <div className="border border-slate-200 bg-white" data-testid="project-request-panel">
          <div className="flex items-center justify-between gap-4 flex-wrap p-5 border-b border-slate-200">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Son Tarih</div>
                <div className="text-sm font-medium mt-0.5">{deadlineLabel(req)}</div>
                <DeadlineWarning deadline={req.deadline} time={req.deadline_time} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Gelen Teklif</div>
                <div className="font-heading font-bold text-2xl tracking-tight" data-testid="project-offer-count">
                  {req.offer_count}<span className="text-sm text-slate-400 font-medium"> / {req.supplier_count}</span>
                </div>
              </div>
              {req.currency && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Para Birimi</div>
                  <div className="text-sm font-medium mt-0.5">{req.currency}</div>
                </div>
              )}
            </div>
            <Link to={`/admin/teklifler/${req.id}`} data-testid="project-compare-link">
              <Button size="sm" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 h-9 font-semibold transition-colors duration-200">
                <Scale size={13} /> Teklifleri Karşılaştır
              </Button>
            </Link>
          </div>
          {req.files?.length > 0 && (
            <div className="px-5 py-4 border-b border-slate-200 space-y-2" data-testid="project-request-files">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Talep Ekleri</div>
              <FileChips files={req.files} />
            </div>
          )}
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-medium">Tedarikçi</th>
                <th className="px-5 py-3 font-medium">Durum</th>
                <th className="px-5 py-3 font-medium text-right">Fiyat</th>
                <th className="px-5 py-3 font-medium text-right">Teklif Tarihi</th>
              </tr>
            </thead>
            <tbody>
              {req.rows.map((row) => (
                <tr key={row.supplier_id} className="border-b border-slate-200 last:border-b-0" data-testid={`project-supplier-row-${row.supplier_id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{row.company_name}</div>
                    <div className="text-xs text-slate-500">{row.contact_name}</div>
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={row.offer_status && row.offer_status !== "pending" ? row.offer_status : row.status} />
                  </td>
                  <td className="px-5 py-3 text-right">
                    {row.price != null ? (
                      <span className="font-heading font-bold">{row.price.toLocaleString("tr-TR")} <span className="text-xs font-medium text-slate-500">{row.currency}</span></span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right text-slate-500">{row.offer_date ? formatDate(row.offer_date) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white p-8 text-center space-y-4" data-testid="project-no-request">
          <p className="text-slate-500 text-sm">Bu proje için henüz teklif talebi oluşturulmadı.</p>
          <Link to="/admin/teklif-al">
            <Button className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 font-semibold transition-colors duration-200">
              <FilePlus size={15} /> Teklif Al
            </Button>
          </Link>
        </div>
      )}

      {project.owner_id && <MessageThread endpoint={`/projects/${id}/messages`} myRole="admin" title="Müşteri Mesajları" emptyText="Müşteri henüz mesaj yazmadı. İlk mesajı siz de gönderebilirsiniz." placeholder="Müşteriye yanıtınızı yazın..." />}
    </div>
  );
}
